import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert } from 'react-native';
import Toast from 'react-native-root-toast';

import {
	CREATE_PLANOGRAM_ITEM,
	DESTROY_PLANOGRAM_ITEM,
	MOVE_PLANOGRAM_ITEM,
} from '../../../common/graphql/planogramItem';
import { resolveNumericValue } from './PointOfSalePlanogramHelpers';

// Market list actions: add an item after another, move it up/down, remove it.
const usePointOfSalePlanogramItemActions = ({
	planogramId,
	canModifyMarketItems,
	refetch,
}) => {
	const [actionItemId, setActionItemId] = useState(null);
	const [createPlanogramItemMutation, { loading: creatingPlanogramItem }] = useMutation(CREATE_PLANOGRAM_ITEM);
	const [movePlanogramItemMutation, { loading: movingPlanogramItem }] = useMutation(MOVE_PLANOGRAM_ITEM);
	const [destroyPlanogramItemMutation, { loading: destroyingPlanogramItem }] = useMutation(DESTROY_PLANOGRAM_ITEM);
	const isMutatingPlanogramItems = creatingPlanogramItem || movingPlanogramItem || destroyingPlanogramItem;

	const executePlanogramMutation = useCallback(async ({
		mutationAction,
		successMessage,
	}) => {
		try {
			await mutationAction();
			await refetch();
			Toast.show(successMessage, {
				duration: Toast.durations.SHORT,
			});
		}
		catch (error) {
			Toast.show('Unable to update planogram.', {
				duration: Toast.durations.SHORT,
			});
		}
		finally {
			setActionItemId(null);
		}
	}, [refetch]);

	const createItemAfter = useCallback(async ({
		anchorItem,
		planogramItemGroupId,
	}) => {
		if (!canModifyMarketItems || !planogramId) {
			return;
		}

		const resolvedRow = resolveNumericValue(anchorItem?.row);
		const resolvedColumn = resolveNumericValue(anchorItem?.column) + 1;
		const variables = {
			planogramId,
			row: resolvedRow,
			column: resolvedColumn,
			...(planogramItemGroupId ? { planogramItemGroupId } : {}),
		};

		setActionItemId(anchorItem?.id || `group-${planogramItemGroupId || 'default'}`);
		await executePlanogramMutation({
			mutationAction: () => createPlanogramItemMutation({ variables }),
			successMessage: 'Planogram item added',
		});
	}, [canModifyMarketItems, createPlanogramItemMutation, executePlanogramMutation, planogramId]);

	const createFirstItem = useCallback(async ({
		planogramItemGroupId,
	}) => {
		return createItemAfter({
			planogramItemGroupId,
			anchorItem: {
				id: `first-item-${planogramItemGroupId || 'default'}`,
				row: 0,
				column: -1,
			},
		});
	}, [createItemAfter]);

	const moveItemByOffset = useCallback(async ({
		item,
		itemList,
		itemIndex,
		offset,
	}) => {
		if (!canModifyMarketItems || !item || !Array.isArray(itemList)) {
			return;
		}

		const targetItem = itemList[itemIndex + offset];
		if (!targetItem) {
			return;
		}

		const variables = {
			id: item.id,
			row: resolveNumericValue(targetItem.row),
			column: resolveNumericValue(targetItem.column),
			...(targetItem.planogramItemGroupId || item.planogramItemGroupId
				? { planogramItemGroupId: targetItem.planogramItemGroupId || item.planogramItemGroupId }
				: {}),
		};

		setActionItemId(item.id);
		await executePlanogramMutation({
			mutationAction: () => movePlanogramItemMutation({ variables }),
			successMessage: 'Planogram item moved',
		});
	}, [canModifyMarketItems, executePlanogramMutation, movePlanogramItemMutation]);

	const removeItem = useCallback(async (item) => {
		if (!canModifyMarketItems || !item?.id) {
			return;
		}

		setActionItemId(item.id);
		await executePlanogramMutation({
			mutationAction: () => destroyPlanogramItemMutation({
				variables: {
					id: item.id,
				},
			}),
			successMessage: 'Planogram item removed',
		});
	}, [canModifyMarketItems, destroyPlanogramItemMutation, executePlanogramMutation]);

	const confirmRemoveItem = useCallback((item) => {
		const itemLabel = item?.product?.name || item?.selection || 'this item';
		Alert.alert(
			'Remove item?',
			`Are you sure you want to remove ${itemLabel}?`,
			[
				{
					text: 'Cancel',
					style: 'cancel',
				},
				{
					text: 'Remove',
					style: 'destructive',
					onPress: () => {
						void removeItem(item);
					},
				},
			],
		);
	}, [removeItem]);

	return {
		actionItemId,
		isMutatingPlanogramItems,
		createItemAfter,
		createFirstItem,
		moveItemByOffset,
		confirmRemoveItem,
	};
};

export default usePointOfSalePlanogramItemActions;
